import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { getMyPaymentHistory, type PaymentHistoryItem } from "../../services/api";
import MobileAppLayout from "../../layouts/MobileAppLayout";
import api from "../../services/api";
import { GameCard, GameHero, GameIcon, SegmentedControl } from "../../components/game";
import { formatDate } from "../../lib/dateFormat";
import PayDueCard from "../../components/app/PayDueCard";

type AthleteProduct = {
  id: string;
  product_id: string;
  due_day: number | null;
  next_due_date: string | null;
  payment_status: "pending" | "paid" | "overdue";
  payment_method: string | null;
  notes: string | null;
  product: {
    id: string;
    name: string;
    description: string | null;
    price_cents: number;
    currency: string;
    recurrence_type: string;
  };
};

type MyProductsResponse = {
  products: AthleteProduct[];
  club: {
    name: string;
    payment_methods: string[] | null;
  } | null;
  /** True for an adult athlete with no parent on file — they pay their own. */
  payer_is_self?: boolean;
};

export default function AppPaymentHistory() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<"pending" | "history">("pending");
  const [myProducts, setMyProducts] = useState<AthleteProduct[]>([]);
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // A child is shown what has been paid; an adult athlete who pays their own
  // fees is also shown what is owed, and a way to settle it.
  const [payerIsSelf, setPayerIsSelf] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(false);
    try {
      const [productsRes, historyRes] = await Promise.all([
        api.get<MyProductsResponse>("/products/my-products"),
        getMyPaymentHistory(),
      ]);
      setMyProducts(productsRes.data.products);
      setPayerIsSelf(productsRes.data.payer_is_self === true);
      setPaymentHistory(historyRes);
    } catch (err) {
      console.error("Failed to load payment data:", err);
      setError(true);
    }
    setLoading(false);
  };

  const formatCurrency = (cents: number, currency = "RON") => {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  };

  const pendingProducts = myProducts.filter(p => p.payment_status !== "paid");
  const totalPending = pendingProducts.reduce((sum, p) => sum + p.product.price_cents, 0);

  return (
    <MobileAppLayout>
      <div className="px-4 py-6 space-y-4">
        {/* Hero header — matches the flow hero used across the app */}
        <GameHero flow className="-mx-4 -mt-6 rounded-br-3xl px-4 pt-3 pb-5">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-extrabold">{t("payments.title", "Plăți")}</h1>
              <p className="text-white/80 text-sm mt-0.5">
                {payerIsSelf
                  ? `${pendingProducts.length} ${
                      pendingProducts.length === 1
                        ? t("payments.service", "serviciu")
                        : t("payments.services", "servicii")
                    }`
                  : t("payments.paidByParent", "Plătite de părinte")}
              </p>
            </div>
            <Link
              to="/app/dashboard"
              className="w-10 h-10 rounded-2xl bg-white/15 hover:bg-white/25 grid place-items-center transition-colors active:translate-y-[2px]"
              aria-label={t("common.close", "Închide")}
            >
              <GameIcon name="x" className="w-5 h-5 text-white" />
            </Link>
          </div>

          {/* Total due — only for the athlete who actually pays it. */}
          {payerIsSelf && pendingProducts.length > 0 && (
            <div className="mt-5 flex items-center gap-3 rounded-2xl bg-white/15 px-4 py-3">
              <div className="w-11 h-11 rounded-2xl bg-amber-400/90 grid place-items-center shadow-[0_3px_0_rgba(0,0,0,0.15)]">
                <GameIcon name="bolt" className="w-6 h-6" />
              </div>
              <div>
                <p className="text-white/80 text-xs font-bold uppercase tracking-wide">{t("payments.totalDue", "Total de plata")}</p>
                <p className="text-2xl font-extrabold tabular-nums leading-tight">{formatCurrency(totalPending)}</p>
              </div>
            </div>
          )}
        </GameHero>

        {/* An adult athlete pays, so they get both tabs. A child gets the
            history — what has been paid is not a burden — and one line telling
            them whose job the rest is. */}
        {payerIsSelf ? (
          <>
            <PayDueCard />
            <SegmentedControl<"pending" | "history">
              className="w-full [&>button]:flex-1"
              value={activeTab}
              onChange={setActiveTab}
              options={[
                { value: "pending", label: `${t("payments.pending", "De plată")} (${pendingProducts.length})` },
                { value: "history", label: `${t("payments.history", "Istoric")} (${paymentHistory.length})` },
              ]}
            />
          </>
        ) : (
          pendingProducts.length > 0 && (
            <GameCard className="p-4">
              <p className="text-sm font-bold text-slate-800">
                {t("payments.parentPaysTitle", "Cotizația o achită părintele")}
              </p>
              <p className="mt-1 text-xs leading-relaxed text-slate-500">
                {t(
                  "payments.parentPaysBody",
                  "Spune-i părintelui tău dacă vezi că termenul s-a apropiat. El o plătește din aplicația lui."
                )}
              </p>
            </GameCard>
          )
        )}

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : error ? (
          <GameCard className="p-6 text-center">
            <h3 className="text-base font-bold text-slate-800 mb-1">{t("common.loadErrorTitle", "Ceva n-a mers")}</h3>
            <p className="text-sm text-slate-500">
              {t("common.loadError", "Nu am putut încărca datele. Verifică conexiunea și încearcă din nou.")}
            </p>
            <button
              onClick={loadData}
              className="mt-3 px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white"
            >
              {t("common.retry", "Reîncearcă")}
            </button>
          </GameCard>
        ) : activeTab === "pending" && payerIsSelf ? (
          /* Pending Payments — only for the athlete who pays them */
          pendingProducts.length === 0 ? (
            <GameCard className="p-8 text-center">
              <div className="w-16 h-16 rounded-3xl bg-emerald-100 grid place-items-center mx-auto mb-4 text-emerald-600 shadow-[0_3px_0_#d1fae5]">
                <GameIcon name="check" className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-extrabold text-slate-800 mb-1">{t("payments.allPaid", "Toate plățile sunt la zi!")}</h3>
              <p className="text-slate-500 text-sm">{t("payments.noOutstanding", "Nu ai plăți restante")}</p>
            </GameCard>
          ) : (
            <div className="space-y-3">
              {pendingProducts.map((ap) => {
                const isOverdue = ap.payment_status === "overdue";
                return (
                  <GameCard
                    key={ap.id}
                    className={`p-4 ${isOverdue ? "ring-1 ring-red-200" : "ring-1 ring-amber-200"}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-extrabold text-slate-800">{ap.product.name}</h4>
                        {ap.product.description && (
                          <p className="text-xs text-slate-500 mt-0.5">{ap.product.description}</p>
                        )}
                      </div>
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${
                        isOverdue ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
                      }`}>
                        <GameIcon name={isOverdue ? "flame" : "clock"} className="w-3.5 h-3.5" />
                        {isOverdue ? t("payment.overdue", "Întârziat") : t("payment.pending", "De plată")}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-lg font-extrabold text-slate-800 tabular-nums">
                          {formatCurrency(ap.product.price_cents, ap.product.currency)}
                        </p>
                        <p className="text-xs text-slate-500">
                          / {ap.product.recurrence_type === "monthly" ? t("common.month", "luna") :
                             ap.product.recurrence_type === "weekly" ? t("common.week", "săptămâna") :
                             t("common.oneTime", "o singura data")}
                        </p>
                      </div>
                      {ap.next_due_date && (
                        <div className="text-right">
                          <p className="text-xs text-slate-500">{t("payment.dueDate", "Scadenta")}</p>
                          <p className={`text-sm font-bold ${isOverdue ? "text-red-600" : "text-slate-700"}`}>
                            {formatDate(new Date(ap.next_due_date))}
                          </p>
                        </div>
                      )}
                    </div>
                  </GameCard>
                );
              })}
            </div>
          )
        ) : (
          /* Payment History */
          paymentHistory.length === 0 ? (
            <GameCard className="p-8 text-center">
              <div className="w-16 h-16 rounded-3xl bg-slate-100 grid place-items-center mx-auto mb-4 text-slate-400 shadow-[0_3px_0_#eef2f8]">
                <GameIcon name="clock" className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-extrabold text-slate-800 mb-1">{t("payments.noHistory", "Niciun istoric")}</h3>
              <p className="text-slate-500 text-sm">{t("payments.noPaymentsMade", "Nu ai efectuat încă o plată")}</p>
            </GameCard>
          ) : (
            <div className="space-y-3">
              {paymentHistory.map((payment) => (
                <GameCard key={payment.id} className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-extrabold text-slate-800">
                        {payment.athlete_product?.product?.name || t("payments.unknownProduct", "Serviciu")}
                      </h4>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {new Date(payment.paid_at).toLocaleDateString("ro-RO", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold">
                      <GameIcon name="check" className="w-3.5 h-3.5" />
                      {t("payment.paid", "Plătit")}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-lg font-extrabold text-emerald-600 tabular-nums">
                      {formatCurrency(payment.amount_cents, payment.currency)}
                    </p>
                    <div className="flex items-center gap-2">
                      {payment.payment_method === "cash" && (
                        <span className="flex items-center gap-1 text-xs text-slate-500">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          {t("payment.cash", "Numerar")}
                        </span>
                      )}
                      {payment.payment_method === "card" && (
                        <span className="flex items-center gap-1 text-xs text-slate-500">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                          </svg>
                          {t("payment.card", "Card")}
                        </span>
                      )}
                      {payment.payment_method === "bank_transfer" && (
                        <span className="flex items-center gap-1 text-xs text-slate-500">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                          </svg>
                          {t("payment.bankTransfer", "Transfer")}
                        </span>
                      )}
                    </div>
                  </div>
                  {payment.notes && (
                    <p className="mt-2 text-xs text-slate-500 italic">{payment.notes}</p>
                  )}
                </GameCard>
              ))}
            </div>
          )
        )}
      </div>
    </MobileAppLayout>
  );
}
